import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  Slider,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tooltip,
  Link
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  Group as CohortsIcon,
  Payment as PaymentIcon,
  TrendingUp as TrendingUpIcon,
  LocationOn as LocationIcon,
  School as EducationIcon,
  Work as WorkIcon,
  Language as LanguageIcon,
  HealthAndSafety as HealthIcon,
  Search as SearchIcon,
  Save as SaveIcon,
  Compare as CompareIcon
} from '@mui/icons-material';
import { MapContainer, TileLayer, ZoomControl, ScaleControl } from 'react-leaflet';
import FilterToggleButton from '../../components/map/FilterToggleButton';
import { filterPanelStyles } from '../../styles/map/filterPanelStyles';

const PopulationCohorts: React.FC = () => {
  const [showFilters, setShowFilters] = useState(true);
  const [timeRange, setTimeRange] = useState('1year');
  const [customDateRange, setCustomDateRange] = useState({ start: new Date(), end: new Date() });
  const [selectedPayors, setSelectedPayors] = useState<string[]>([]);
  const [selectedHealthPlans, setSelectedHealthPlans] = useState<string[]>([]);
  const [incomeRange, setIncomeRange] = useState<number[]>([0, 200000]);
  const [educationLevel, setEducationLevel] = useState<string[]>([]);
  const [employmentStatus, setEmploymentStatus] = useState<string[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [healthLiteracy, setHealthLiteracy] = useState<string[]>([]);
  const [showDashboard, setShowDashboard] = useState(false);
  const [showMapOverlay, setShowMapOverlay] = useState(false);
  const [selectedSex, setSelectedSex] = useState<string[]>([]);
  const [selectedRace, setSelectedRace] = useState<string[]>([]);
  const [selectedContractTypes, setSelectedContractTypes] = useState<string[]>([]);
  const [selectedHealthMetrics, setSelectedHealthMetrics] = useState<string[]>([]);

  const renderDemographicsFilters = () => (
    <Accordion defaultExpanded>
      <AccordionSummary 
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 56,
          '& .MuiAccordionSummary-content': {
            m: 0
          }
        }}
      >
        <Typography 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontWeight: 500,
            fontSize: '0.875rem'
          }}
        >
          <CohortsIcon fontSize="small" color="primary" />
          Demographics
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2, pt: 0 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Age Range
            </Typography>
            <Slider
              value={[0, 100]}
              onChange={() => {}}
              valueLabelDisplay="auto"
              min={0}
              max={100}
            />
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Sex
            </Typography>
            <FormControl fullWidth size="small">
              <Select 
                multiple 
                value={selectedSex}
                onChange={(e) => setSelectedSex(e.target.value as string[])}
              >
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Race/Ethnicity
            </Typography>
            <FormControl fullWidth size="small">
              <Select 
                multiple 
                value={selectedRace}
                onChange={(e) => setSelectedRace(e.target.value as string[])}
              >
                <MenuItem value="white">White</MenuItem>
                <MenuItem value="black">Black/African American</MenuItem>
                <MenuItem value="hispanic">Hispanic/Latino</MenuItem>
                <MenuItem value="asian">Asian</MenuItem>
                <MenuItem value="native">Native American</MenuItem>
                <MenuItem value="pacific">Pacific Islander</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Household Size
            </Typography>
            <Slider
              value={[1, 10]}
              onChange={() => {}}
              valueLabelDisplay="auto"
              min={1}
              max={10}
              marks
            />
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );

  const renderPayorMixFilters = () => (
    <Accordion defaultExpanded>
      <AccordionSummary 
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 56,
          '& .MuiAccordionSummary-content': {
            m: 0
          }
        }}
      >
        <Typography 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontWeight: 500,
            fontSize: '0.875rem'
          }}
        >
          <PaymentIcon fontSize="small" color="primary" />
          Payor Mix
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2, pt: 0 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Insurance Type
            </Typography>
            <FormControl fullWidth size="small">
              <Select multiple value={selectedPayors} onChange={(e) => setSelectedPayors(e.target.value as string[])}>
                <MenuItem value="commercial">Commercial</MenuItem>
                <MenuItem value="medicare">Medicare</MenuItem>
                <MenuItem value="medicaid">Medicaid</MenuItem>
                <MenuItem value="uninsured">Uninsured</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Contract Type
            </Typography>
            <FormControl fullWidth size="small">
              <Select 
                multiple 
                value={selectedContractTypes}
                onChange={(e) => setSelectedContractTypes(e.target.value as string[])}
              >
                <MenuItem value="ffs">Fee-for-Service</MenuItem>
                <MenuItem value="risk">Risk Contracts</MenuItem>
                <MenuItem value="value">Value-Based</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Health Plans
            </Typography>
            <FormControl fullWidth size="small">
              <Select multiple value={selectedHealthPlans} onChange={(e) => setSelectedHealthPlans(e.target.value as string[])}>
                {/* Populate with actual health plans from data */}
                <MenuItem value="plan1">Plan A</MenuItem>
                <MenuItem value="plan2">Plan B</MenuItem>
                <MenuItem value="plan3">Plan C</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );

  const renderSDOHFilters = () => (
    <Accordion defaultExpanded>
      <AccordionSummary 
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 56,
          '& .MuiAccordionSummary-content': {
            m: 0
          }
        }}
      >
        <Typography 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontWeight: 500,
            fontSize: '0.875rem'
          }}
        >
          <HealthIcon fontSize="small" color="primary" />
          Social Determinants of Health
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2, pt: 0 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Income Level
            </Typography>
            <Slider
              value={incomeRange}
              onChange={(_, value) => setIncomeRange(value as number[])}
              valueLabelDisplay="auto"
              min={0}
              max={200000}
              valueLabelFormat={(value) => `$${value.toLocaleString()}`}
            />
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Education Level
            </Typography>
            <FormControl fullWidth size="small">
              <Select multiple value={educationLevel} onChange={(e) => setEducationLevel(e.target.value as string[])}>
                <MenuItem value="less_than_hs">Less than High School</MenuItem>
                <MenuItem value="hs">High School</MenuItem>
                <MenuItem value="some_college">Some College</MenuItem>
                <MenuItem value="associates">Associate's Degree</MenuItem>
                <MenuItem value="bachelors">Bachelor's Degree</MenuItem>
                <MenuItem value="masters">Master's Degree</MenuItem>
                <MenuItem value="doctorate">Doctorate</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Employment Status
            </Typography>
            <FormControl fullWidth size="small">
              <Select multiple value={employmentStatus} onChange={(e) => setEmploymentStatus(e.target.value as string[])}>
                <MenuItem value="employed">Employed</MenuItem>
                <MenuItem value="unemployed">Unemployed</MenuItem>
                <MenuItem value="retired">Retired</MenuItem>
                <MenuItem value="disabled">Disabled</MenuItem>
                <MenuItem value="student">Student</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Language Spoken
            </Typography>
            <FormControl fullWidth size="small">
              <Select multiple value={languages} onChange={(e) => setLanguages(e.target.value as string[])}>
                <MenuItem value="english">English</MenuItem>
                <MenuItem value="spanish">Spanish</MenuItem>
                <MenuItem value="mandarin">Mandarin</MenuItem>
                <MenuItem value="vietnamese">Vietnamese</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Health Literacy Level
            </Typography>
            <FormControl fullWidth size="small">
              <Select multiple value={healthLiteracy} onChange={(e) => setHealthLiteracy(e.target.value as string[])}>
                <MenuItem value="basic">Basic</MenuItem>
                <MenuItem value="intermediate">Intermediate</MenuItem>
                <MenuItem value="proficient">Proficient</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );

  const renderPopulationTrendsFilters = () => (
    <Accordion defaultExpanded>
      <AccordionSummary 
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 56,
          '& .MuiAccordionSummary-content': {
            m: 0
          }
        }}
      >
        <Typography 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontWeight: 500,
            fontSize: '0.875rem'
          }}
        >
          <TrendingUpIcon fontSize="small" color="primary" />
          Population Trends
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2, pt: 0 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Time Period
            </Typography>
            <FormControl fullWidth size="small">
              <Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
                <MenuItem value="1month">Last Month</MenuItem>
                <MenuItem value="6months">Last 6 Months</MenuItem>
                <MenuItem value="1year">Last Year</MenuItem>
                <MenuItem value="5years">Last 5 Years</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Migration Patterns
            </Typography>
            <FormControlLabel
              control={<Switch size="small" />}
              label="Show Migration Overlay"
            />
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Health Metrics
            </Typography>
            <FormControl fullWidth size="small">
              <Select 
                multiple 
                value={selectedHealthMetrics}
                onChange={(e) => setSelectedHealthMetrics(e.target.value as string[])}
              >
                <MenuItem value="conditions">Common Conditions</MenuItem>
                <MenuItem value="status">Health Status</MenuItem>
                <MenuItem value="ed">ED Visits</MenuItem>
                <MenuItem value="births">Live Births</MenuItem>
                <MenuItem value="deaths">Deaths</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );

  const renderCohortActions = () => (
    <Accordion defaultExpanded>
      <AccordionSummary 
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 56,
          '& .MuiAccordionSummary-content': {
            m: 0
          }
        }}
      >
        <Typography 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontWeight: 500,
            fontSize: '0.875rem'
          }}
        >
          <CohortsIcon fontSize="small" color="primary" />
          Cohort Actions
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2, pt: 0 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Load Existing Cohort
            </Typography>
            <FormControl fullWidth size="small">
              <Select>
                <MenuItem value="cohort1">High-Risk Patients</MenuItem>
                <MenuItem value="cohort2">Chronic Care</MenuItem>
                <MenuItem value="cohort3">Preventive Care</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Find Cohorts
            </Typography>
            <FormControl fullWidth size="small">
              <Select>
                <MenuItem value="revenue">Highest Revenue</MenuItem>
                <MenuItem value="volume">Highest Volume</MenuItem>
                <MenuItem value="lives">Largest Patient Base</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            fullWidth
          >
            Create New Cohort
          </Button>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Box sx={{ height: 'calc(100vh - 240px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom>Population & Cohorts</Typography>
          <Typography variant="body2" color="text.secondary">
            Analyze population health segments and manage patient cohorts
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <FilterToggleButton showFilters={showFilters} onToggle={() => setShowFilters(!showFilters)} />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="small"
          >
            New Cohort
          </Button>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ 
        flexGrow: 1,
        minHeight: 0,
        display: 'flex',
        gap: 0
      }}>
        {/* Filters Panel */}
        <Paper sx={filterPanelStyles.panel({ showFilters })}>
          {showFilters && (
            <Box sx={filterPanelStyles.container}>
              <Box sx={filterPanelStyles.content}>
                <Card 
                  elevation={0} 
                  sx={{ 
                    borderRadius: 0,
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <CardContent sx={{ py: 2 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        fontSize: '1.1rem',
                        fontWeight: 500
                      }}
                    >
                      <FilterIcon fontSize="small" /> Population Filters
                    </Typography>
                  </CardContent>
                </Card>

                <Box sx={{ 
                  flex: 1, 
                  overflowY: 'auto',
                  p: 2
                }}>
                  {renderDemographicsFilters()}
                  {renderPayorMixFilters()}
                  {renderSDOHFilters()}
                  {renderPopulationTrendsFilters()}
                  {renderCohortActions()}
                </Box>
              </Box>
            </Box>
          )}
        </Paper>

        {/* Map Container */}
        <Paper 
          elevation={3} 
          sx={{ 
            flexGrow: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: showFilters ? '0 4px 4px 0' : '4px'
          }}
        >
          <Box sx={{ p: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <FormControlLabel
                control={
                  <Switch 
                    size="small" 
                    checked={showDashboard}
                    onChange={(e) => setShowDashboard(e.target.checked)}
                  />
                }
                label="Show Dashboard"
              />
              <FormControlLabel
                control={
                  <Switch 
                    size="small" 
                    checked={showMapOverlay}
                    onChange={(e) => setShowMapOverlay(e.target.checked)}
                  />
                }
                label="Show Map Overlay"
              />
            </Stack>
          </Box>
          <Box sx={{ flexGrow: 1, position: 'relative' }}>
            <MapContainer
              center={[28.5383, -81.3792]}
              zoom={10}
              scrollWheelZoom={true}
              zoomControl={false}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <ZoomControl position="topright" />
              <ScaleControl position="bottomright" />
            </MapContainer>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default PopulationCohorts; 